/**
 * DOM utilities for AG-UI Chat.
 *
 * Helper functions for creating and manipulating chat elements.
 */

import { type Message } from '@ag-ui/core';
import { renderMarkdown } from './markdown';

/**
 * Default configuration values for the chat component.
 */
export const DEFAULT_CHAT_CONFIG = {
  debug: false,
  scrollToBottom: true,
  groupConversations: true,
  maxMessages: 1,
  streamTimeout: 30000,
} as const;

/**
 * Chat behavior configuration options.
 */
export interface ChatBehaviorConfig {
  /** Enable debug mode for verbose logging */
  debug: boolean;
  /** Automatically scroll to bottom on new messages */
  scrollToBottom: boolean;
  /** Wrap user/assistant message pairs in a conversation div */
  groupConversations: boolean;
  /** Maximum number of messages to send with each request (0 for unlimited) */
  maxMessages: number;
  /** Timeout in milliseconds for the SSE stream. If no events are received within this period, the stream is aborted and the chat is reset. Set to 0 to disable. (default: 30000) */
  streamTimeout: number;
}

/**
 * Configuration for the chat component.
 */
export interface ChatConfig {
  endpoint: string;
  runnerEndpoint?: string;
  agentId: string;
  tokenEndpoint?: string;
  /** Behavior configuration options */
  config: ChatBehaviorConfig;
}

/**
 * Parses chat configuration from a container element's data attributes.
 */
export function parseChatConfig(container: HTMLElement): ChatConfig {
  // Parse config from data attribute if present
  let userConfig: Partial<ChatBehaviorConfig> = {};
  if (container.dataset.config) {
    try {
      userConfig = JSON.parse(container.dataset.config);
    } catch (e) {
      console.warn('AG-UI Chat: Invalid config JSON, using defaults');
    }
  }

  // Merge user config with defaults
  const config: ChatBehaviorConfig = {
    ...DEFAULT_CHAT_CONFIG,
    ...userConfig,
  };

  return {
    endpoint: container.dataset.endpoint || '/agui/api/chat',
    runnerEndpoint: container.dataset.runnerEndpoint || undefined,
    agentId: container.dataset.agentId || '',
    tokenEndpoint: container.dataset.tokenEndpoint || undefined,
    config,
  };
}

/**
 * Creates a message element for display in the chat.
 */
export function createMessageElement(message: Message): HTMLElement {
  const messageEl = document.createElement('div');
  messageEl.classList.add('agui-chat__message');
  messageEl.classList.add(`agui-chat__message--${message.role}`);
  messageEl.dataset.messageId = message.id;

  // Create content wrapper for styling
  const contentEl = document.createElement('div');
  contentEl.classList.add('agui-chat__message-content');

  // Get raw text and render as HTML (with basic markdown support)
  const text = getMessageText(message);
  contentEl.innerHTML = renderMarkdown(text);

  messageEl.appendChild(contentEl);
  return messageEl;
}

/**
 * Extracts text content from a Message object.
 * Handles both string content and array of content parts.
 */
export function getMessageText(message: Message): string {
  if (typeof message.content === 'string') {
    return message.content;
  }
  if (Array.isArray(message.content)) {
    return message.content
      .filter((item): item is { type: 'text'; text: string } => item.type === 'text')
      .map((item) => item.text)
      .join('');
  }
  return '';
}

/**
 * Updates a message element's content with new text.
 */
export function updateMessageContent(messageEl: HTMLElement, content: string): void {
  const contentEl = messageEl.querySelector('.agui-chat__message-content');
  if (contentEl) {
    contentEl.innerHTML = renderMarkdown(content);
  }
}

/**
 * Scrolls a container to the bottom.
 */
export function scrollToBottom(container: HTMLElement): void {
  container.scrollTop = container.scrollHeight;
}

/**
 * Required DOM elements for the chat component.
 */
export interface ChatElements {
  container: HTMLElement;
  messagesContainer: HTMLElement;
  form: HTMLFormElement;
  input: HTMLInputElement;
}

/**
 * Queries and validates required chat elements from a container.
 * Returns null if any required element is missing.
 */
export function queryChatElements(container: HTMLElement): ChatElements | null {
  const messagesContainer = container.querySelector('.agui-chat__messages');
  const form = container.querySelector('.agui-chat__input-form');
  const input = container.querySelector('.agui-chat__input');

  if (!messagesContainer || !form || !input) {
    console.error('AG-UI Chat: Missing required elements');
    return null;
  }

  return {
    container,
    messagesContainer: messagesContainer as HTMLElement,
    form: form as HTMLFormElement,
    input: input as HTMLInputElement,
  };
}

